import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Timestamp } from 'firebase/firestore';
import { useDateFormatter } from '../lib/useIdeas';

/**
 * One card in a list of notes — personal on `/`, collaborative on `/collab`.
 *
 * Takes fields rather than a document so both shapes fit, and leaves the lightbox to the
 * list: one dialog per screen, not one per card.
 */
export default function NoteCard({
  to,
  title,
  description,
  images,
  tags,
  date,
  links,
  extra,
  onViewImage,
}: {
  to: string;
  title?: string;
  description?: string;
  images?: string[];
  tags?: string[];
  date?: Timestamp;
  links?: string[];
  /** Appended to the meta row — who shared it, how many are on it. */
  extra?: ReactNode;
  onViewImage: (images: string[], start: number) => void;
}) {
  const fmt = useDateFormatter();
  return (
    <li>
      {/* The title is the way in. Kept in --text rather than --accent so the card hierarchy
          survives; the hover state is what signals the link. */}
      <h2>
        <Link to={to}>{title || 'Untitled'}</Link>
      </h2>
      {description && <p>{description}</p>}
      {images && images.length > 0 && (
        <div className="thumbs">
          {images.map((src, i) => (
            /* The wrapper is the 96px box and the image fills it — the same shape the editor
               uses. An <img> carrying .thumb itself cannot work: the `.thumb img` rule that
               makes the picture fill its box also matches it, and width/height 100% then
               overrides the box's own size. */
            <div className="thumb" key={src}>
              <button
                type="button"
                className="thumb-open"
                aria-label={`View image ${i + 1} of ${images.length}`}
                onClick={() => onViewImage(images, i)}
              >
                {/* alt="" — these are the note's own photos, already described by its title
                    and description; a URL read aloud would be noise. */}
                <img src={src} alt="" loading="lazy" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="meta">
        {tags?.map((tag) => (
          <span className="tag" key={tag}>{tag}</span>
        ))}
        {date && <time>{fmt.format(date.toDate())}</time>}
        {extra}
      </div>
      {links && links.length > 0 && (
        <ul className="links">
          {links.map((href) => (
            <li key={href}>
              {/* noreferrer as well as noopener: these URLs come from transcribed speech and
                  are not vetted. */}
              <a href={href} target="_blank" rel="noopener noreferrer">{href}</a>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
